<template>
  <authenticated-layout
    :back-route="{ name: 'institutions' }"
    :breadcrumbs="breadcrumbItems">

    <page-header :title="institution?.title">
      <template #actions>
        <v-menu
          bottom
          transition="scale-transition"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="outlined"
              color="primary"
              prepend-icon="fa fa-cog"
              data-pw="menuButton"
            >
              Settings
            </v-btn>
          </template>
          <v-list min-width="250px">
            <v-list-item
              :disabled="!isAdminOrRoot && !store.hasAbility(Entity.Institution, Action.edit)"
              @click="showEditInstitution = true"
            >
              {{ $t("buttons.edit") }}
            </v-list-item>
            <v-list-item
              :disabled="!isAdminOrRoot"
              @click="showMoveInstitution = true"
            >
              Move
            </v-list-item>
            <v-list-item
              :disabled="!isAdminOrRoot && !store.hasAbility(Entity.Institution, Action.edit)"
              @click="showEditCurriculum = true">
              {{ $t("components.InstitutionDetailPage.manage_curriculum") }}
            </v-list-item>
            <v-list-item
              :disabled="!isAdminOrRoot && !store.hasAbility(Entity.Cohort, Action.create)"
              @click="showCreateDialog = true">
              {{ $t("components.InstitutionDetailPage.create_cohort") }}
            </v-list-item>
            <v-list-item
              :disabled="!isAdminOrRoot && !store.hasAbility(Entity.Institution, Action.delete)"
              @click="showDeleteInstitution = true">
              <span class="text-red">{{ $t("buttons.delete") }}</span>
            </v-list-item>
          </v-list>
        </v-menu>

      </template>
    </page-header>

    <v-container class="mt-0 mx-auto pa-0 border-b mb-6" fluid>
      <v-tabs v-model="activeTab" grow>
        <v-tab value="Dashboard">
          Overview
        </v-tab>
        <v-tab value="Cohorts">
          Cohorts
        </v-tab>
        <v-tab value="Curriculum">
          Curriculum
        </v-tab>
        <v-tab value="Requests">
          Join Requests
        </v-tab>
        <v-tab value="Integrations">
          Integrations
        </v-tab>
        <v-tab v-if="isAdminOrRoot" value="Managers">Managers</v-tab>
      </v-tabs>
    </v-container>

    <v-container v-if="institution" fluid class="py-4">

      <v-row v-if="activeTab === 'Dashboard'">
        <v-col cols="9">
          <h1>Insights coming soon</h1>
        </v-col>
        <v-col cols="3">
          <institution-details
            :title="institution.title"
            :slug="institution.slug"
            :tenant="tenantTitle"
            @edit="showEditInstitution = true"
          />
        </v-col>
      </v-row>

      <v-row v-if="activeTab === 'Cohorts'">
        <v-col cols="12">
          <cohort-data-table :institution="institution" />
        </v-col>
      </v-row>

      <v-row  v-if="activeTab === 'Curriculum'">
        <v-col cols="12">
          <institution-curricula :curricula="institution.curricula"/>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <managers-table v-if="isAdminOrRoot && activeTab === 'Managers'" @revoked="fetchInstitution"
                          :managers="managers as User[]"
                          :entity-type="Entity.Institution"/>
        </v-col>
      </v-row>

      <v-row v-if="activeTab === 'Requests'">
        <v-col cols="12">
          <v-alert color="info" density="compact" v-if="!institution.allowsJoinRequests">
            {{ $t("components.JoinRequests.disabled") }}
            <v-btn
              variant="plain"
              @click="showEditInstitution = true"
              icon="fa fa-gear"
            />
          </v-alert>
          <template v-else>
            <div v-if="institution.joinRequests.length > 0">
              <join-request
                v-for="userId in institution.joinRequests"
                :key="userId"
                :user-id="userId"
                :institution-id="institution._id"
                @removed="fetchInstitution"
              />
            </div>
            <div v-else>
              {{ $t("components.JoinRequests.noRequests") }}
            </div>
          </template>
        </v-col>
      </v-row>

      <v-row v-if="activeTab === 'Integrations'">
        <v-col cols="12">
          <h2>{{ $t("components.InstitutionDetailPage.d6_details") }}</h2>
        </v-col>
        <v-col cols="12">
          <D6Details v-if="institution.d6" :institution="institution"/>
        </v-col>
        <v-col cols="12">
          <D6Connection :institution="institution" @connect="(schoolId) => connectD6(schoolId)"
                        @disconnect="disconnectD6"/>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="showEditCurriculum">
      <edit-institution-curriculum
        v-if="institution"
        @close="onEditCurriculumDialog()"
        :title="institution ? institution.title : ''"
        :curricula="institution.curricula"
      />
    </v-dialog>

    <v-dialog v-model="showDeleteInstitution" max-width="500">
      <delete-institution
        v-if="institution"
        @close="showDeleteInstitution = false"
        :confirm-string="institution ? institution.title : ''"
        :message="$t('components.DeleteInstitution.warning_message')"
      />
    </v-dialog>

    <v-dialog v-model="showEditInstitution" max-width="500">
      <edit-institution
        @close="onEditDialog()"
        :institution="institution!"
      />
    </v-dialog>

    <v-dialog v-model="showMoveInstitution" max-width="500">
      <move-institution
        v-if="institution"
        @close="showMoveInstitution = false"
        :institution="institution"/>
    </v-dialog>

    <v-dialog v-model="showCreateDialog" max-width="500">
      <create-tenant-institution-cohort
        :entity-title="$t('components.CreateCards.CreateTenantInstitutionCohort.titleCreateCohort')"
        :institution-id="id"
        :entity-type="Entity.Cohort"
        :allowed="store.hasAbility(Entity.Cohort, Action.create) || isAdminOrRoot"
        data-pw="createCohortCard"
        @close="showCreateDialog = false"
      />
    </v-dialog>
  </authenticated-layout>
</template>

<script setup lang="ts">
import AuthenticatedLayout from "@/layout/AuthenticatedLayout.vue";
import {computed, onMounted, ref, watch} from "vue";
import {useRoute} from "vue-router";
import yapi from "@/lib/yapi";
import {Action, Entity, Institution, isYapiError, Role, User, YapiError} from "@YenzaCT/sdk";
import InstitutionDetails from "@/pages/Manage/InstitutionDetailPage/components/InstitutionDetails.vue";
import InstitutionCurricula from "@/pages/Manage/InstitutionDetailPage/components/InstitutionCurricula.vue";
import DeleteInstitution from "@/pages/Manage/InstitutionDetailPage/components/DeleteInstitution.vue";
import EditInstitution from "@/pages/Manage/InstitutionDetailPage/components/EditInstitution.vue";
import {useGlobalStore} from "@/store";
import EditInstitutionCurriculum from "@/pages/Manage/InstitutionDetailPage/components/EditInstitutionCurriculum.vue";
import ManagersTable from "@/components/ManagersTable.vue";
import D6Details from "@/pages/Manage/InstitutionDetailPage/components/D6Details.vue";
import D6Connection from "@/pages/Manage/InstitutionDetailPage/components/D6Connection.vue";
import JoinRequest from "@/pages/Manage/InstitutionDetailPage/components/JoinRequest.vue";
import CreateTenantInstitutionCohort from "@/components/CreateCards/CreateTenantInstitutionCohortCard.vue";
import CohortDataTable from "@/components/DataTables/CohortDataTable.vue";
import PageHeader from "@/components/PageHeader.vue";
import MoveInstitution from "@/pages/Manage/InstitutionDetailPage/components/MoveInstitution.vue";

const store = useGlobalStore();
const route = useRoute();

const isAdminOrRoot = ref(store.user?.app.role === Role.Admin || store.user?.app.role === Role.Root);
const institution = ref<Institution>();
const managers = ref<User[]>([]);
const tenantTitle = ref<string>();
const showDeleteInstitution = ref(false);
const showEditInstitution = ref(false);
const showMoveInstitution = ref(false);
const showEditCurriculum = ref(false);
const showCreateDialog = ref(false);
const id = route.params.institutionId as string;
const activeTab = ref("Dashboard");

const breadcrumbItems = computed(() => [
  { text: "Institutions", to: { name: "institutions" } },
  { text: institution.value?.title || "" }
]);

async function fetchInstitution() {
  try {
    store.networkBusy = true;
    institution.value = (await yapi.admin.institution.get(id)).data;
    managers.value = (await yapi.admin.managers.list(Entity.Institution, id)).data;
    if (institution.value && typeof institution.value.tenant !== "string") {
      tenantTitle.value = institution.value.tenant.title;
    } else if (typeof institution.value.tenant === "string") {
      tenantTitle.value = institution.value.tenant;
    }
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    store.networkBusy = false;
  }
}

onMounted(async () => {
  await fetchInstitution();
});

watch(() => route.params.id, async (newId, oldId) => {
  if (newId !== oldId) {
    await fetchInstitution();
  }
});

async function onEditCurriculumDialog() {
  showEditCurriculum.value = false;
  await fetchInstitution();
}

async function onEditDialog() {
  showEditInstitution.value = false;
  await fetchInstitution();
}

async function connectD6(schoolId: string) {
  try {
    await yapi.admin.institution.d6.connect(id, schoolId);
    await fetchInstitution();
    store.showSnackbar("School connected");
  } catch (e: unknown) {
    const err = e as YapiError;
    if (err.response) {
      const res = err.response;
      if (res.status === 403) {
        store.showSnackbar("You do not have permission to connect to this school.");
      } else {
        store.showSnackbar("Something went wrong connecting to this school.");
      }
    }
  }
}

async function disconnectD6() {
  await yapi.admin.institution.d6.disconnect(id);
  await fetchInstitution();
}
</script>
